import { z } from 'zod';
import { CurrencySchema, PersonOptionSchema, decimalStr, isoDateStr, nullableStr, } from '../_shared/schemas.js';
/**
 * KAMPÁNY-ELŐREJELZÉS — a `support.campaigns` [T, SHARED] busz-erőforrás szerződése. Az Odin-oldali modell
 * és az indoklás: `prisma/schema/support.prisma` → `Campaign`.
 *
 * KÉT DOLOG, AMI MÁSHOGY VAN, MINT AMIRE SZÁMÍTANÁL:
 *   • a soron VAN `accountId`/`clientId`, MÉGSEM tenant-erőforrás — a modul belső, a partner nem látja;
 *     a két mező hivatkozás (csoportosítás/szűrés), nem izolációs határ,
 *   • a STÁTUSZ (tervezett/fut/lezárt) SZÁMÍTOTT, nem tárolt: a BFF a két dátumból vezeti le minden
 *     olvasáskor. Ezért a `status` a sor-sémában szerepel, de EGYIK írás-body-ban sem.
 */
/** A kampány SZÁMÍTOTT állapota a mai naphoz képest (`Europe/Budapest`) — sosem tárolt, sosem írható. */
export const CampaignStatusSchema = z.enum(['planned', 'running', 'closed']);
/** A rendezés-kulcsok; ismeretlen érték → `startsOn` (a BFF esik vissza, nem hibázik). */
export const CampaignSortSchema = z.enum([
    'startsOn',
    'endsOn',
    'name',
    'code',
    'accountName',
    'ownerName',
    'plannedBudget',
]);
/**
 * Lista-sor. A `version` az OCC-hez kell (a szerkesztés `If-Match`-csel megy), a `*Name` mezők a BFF-ben
 * feloldott nevek — `null`, ha a hivatkozott entitás törölt/feloldhatatlan (a felület „Törölt …" címkét
 * mutat; a nyers UUID SOHA nem megy név-fallbackként).
 */
export const CampaignRowSchema = z.object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    clientId: z.string(),
    clientName: z.string().nullable(),
    accountId: z.string(),
    accountName: z.string().nullable(),
    ownerId: z.string(),
    owner: PersonOptionSchema.nullable(),
    startsOn: isoDateStr,
    endsOn: isoDateStr,
    status: CampaignStatusSchema,
    plannedBudget: z.string().nullable(),
    actualBudget: z.string().nullable(),
    currency: CurrencySchema,
    asanaTaskUrl: z.string().nullable(),
    version: z.number(),
});
/** Részlet: a sor + a leírás + a hívó JOGAI (hogy a felület ne kínáljon 403-ba futó gombot). */
export const CampaignDetailSchema = CampaignRowSchema.extend({
    description: z.string().nullable(),
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
    canManage: z.boolean(),
    canDelete: z.boolean(),
});
/**
 * A kampány-mag, amit a felhasználó szerkeszt. A `clientId` SZÁNDÉKOSAN nincs benne: az ügyfél a fiókból
 * derivált érték, a BFF oldja fel — ha a kliens küldhetné, egy elrontott pár a rossz ügyfélhez könyvelné
 * a büdzsét. A `code` sem szerkeszthető (a BFF generálja), a `status` pedig számított.
 */
const CampaignCoreSchema = z.object({
    name: z.string().trim().min(1).max(160),
    ownerId: z.string().uuid(),
    startsOn: isoDateStr,
    endsOn: isoDateStr,
    description: nullableStr(5000),
    plannedBudget: decimalStr.nullable().optional(),
    actualBudget: decimalStr.nullable().optional(),
    currency: CurrencySchema.default('HUF'),
    asanaTaskUrl: nullableStr(500),
});
/**
 * Felvétel. A `accountIds` TÖBB fiókot is elfogad: ugyanaz a kampány (Black Friday) jellemzően több
 * szállodán fut, és mivel egy kampány egy fiókhoz tartozik, fiókonként KÜLÖN sor keletkezik — azonos
 * névvel/időszakkal/felelőssel, de saját kóddal és saját, később külön szerkeszthető büdzsével.
 * A felső korlát 50: a művelet a buszon fiókonként egy írás, és a BFF nem nyit tranzakciót.
 */
export const CampaignCreateBodySchema = CampaignCoreSchema.extend({
    accountIds: z.array(z.string().uuid()).min(1).max(50),
});
/** Szerkesztés: pontosan EGY fiók (a tömeges felvétel után a sorok külön élnek). */
export const CampaignUpdateBodySchema = CampaignCoreSchema.extend({
    accountId: z.string().uuid(),
});
/**
 * A felvétel válasza. A művelet NEM ATOMI (a BFF nem nyit tranzakciót az Odin buszán), ezért a válasz
 * KIMONDJA, mi jött létre és mi nem — a felület a hibás fiókokat kiemeli. Ha egyetlen sor sem jött létre,
 * a végpont hibakódot ad, nem üres `created` listát.
 */
export const CampaignCreateResponseSchema = z.object({
    created: z.array(CampaignRowSchema),
    failed: z.array(z.object({
        accountId: z.string(),
        accountName: z.string().nullable(),
        message: z.string(),
    })),
});
/**
 * Lista-lekérdezés. A `from`/`to` ÁTFEDÉS-szűrő: az a kampány kell, ami a megadott intervallumba BELELÓG
 * (`startsOn <= to && endsOn >= from`), nem az, ami teljesen benne van — az idősáv a látható tartományra
 * kérdez, és egy átnyúló kampány sávjának ott kell lennie.
 */
export const CampaignListQuerySchema = z.object({
    q: z.string().trim().max(200).optional(),
    clientId: z.string().optional(),
    accountId: z.string().optional(),
    ownerId: z.string().optional(),
    status: CampaignStatusSchema.optional(),
    from: isoDateStr.optional(),
    to: isoDateStr.optional(),
    sort: z.string().optional(),
    order: z.enum(['asc', 'desc']).optional(),
    limit: z.coerce.number().int().positive().max(200).optional(),
    offset: z.coerce.number().int().nonnegative().optional(),
});
export const CampaignListResponseSchema = z.object({
    campaigns: z.array(CampaignRowSchema),
    total: z.number(),
    nextOffset: z.number().nullable(),
});
/**
 * A felvevő űrlap forrásai. Az `accounts` a `clientId`-t is hozza (kaszkádoló szűkítés: ügyfél → fiókjai),
 * az `owners` pedig CSAK aktív belső identityket (partner- és gépi fiók nem lehet kampány-felelős) — a
 * `lang` a név-sorrendet dönti el, és ezért a cache-kulcs része is.
 */
export const CampaignRefsSchema = z.object({
    clients: z.array(z.object({ id: z.string(), name: z.string() })),
    accounts: z.array(z.object({ id: z.string(), name: z.string(), clientId: z.string() })),
    owners: z.array(PersonOptionSchema),
});
//# sourceMappingURL=schemas.js.map